import type { ReactNode } from "react"
import type { UIMessage } from "@ai-sdk/react"
import {
    componentStyles,
    Spinner,
    TextNote,
    VerticalStack,
} from "@mallardbay/lib-react-components"
import React from "react"

import ChatErrorBanner from "~components/shared/chat/chat-error-banner"
import ChatMessageList from "~components/shared/chat/chat-message-list"
import { useChat } from "~components/shared/chat/chat-provider"
import { isMessageFromAi } from "~components/shared/chat/chat-helpers"
import useAutoPreviewSuggestion from "~utils/hooks/use-auto-preview-suggestion"

interface Props {
    readonly messages: UIMessage[]
    readonly isLoading: boolean
    readonly error?: Error
    readonly onRetry?: () => void
    readonly shouldAutoPreviewSuggestions?: boolean
    readonly emptyStateMessage?: string
    readonly emptyStateContent?: ReactNode
}

export default function ChatMessages({
    messages,
    isLoading,
    error,
    onRetry,
    shouldAutoPreviewSuggestions = true,
    emptyStateMessage,
    emptyStateContent,
}: Props) {
    const styles = useStyles()
    const { setSelectedAiSuggestionId, lastSelectedAiSuggestionId } = useChat()

    const shouldShowInstructions = !isLoading && !messages.length && !error
    const shouldShowInitialLoading = isLoading && !messages.length && !error
    const lastMessage = messages[messages.length - 1]
    const isWaitingForAiResponse =
        isLoading && (!lastMessage || !isMessageFromAi(lastMessage))

    useAutoPreviewSuggestion({
        shouldAutoPreviewSuggestions,
        messages,
        setSelectedAiSuggestionId,
        lastSelectedAiSuggestionId,
    })

    return (
        <VerticalStack style={styles.root}>
            {shouldShowInitialLoading && <Spinner shouldUseTopMargin={false} />}
            {shouldShowInstructions && <TextNote>{emptyStateMessage}</TextNote>}
            {shouldShowInstructions && emptyStateContent}
            {messages.length > 0 && (
                <ChatMessageList
                    messages={messages}
                    isWaitingForAiResponse={isWaitingForAiResponse}
                />
            )}
            {error && <ChatErrorBanner error={error} onRetry={onRetry} />}
        </VerticalStack>
    )
}

function useStyles() {
    return componentStyles({
        root: {
            overflowY: "auto",
            flex: 1,
            position: "relative",
            padding: 4,
            paddingBottom: 8,
            // Reserves space between scroll targets and the viewport's bottom
            // edge. Without this, `scrollIntoView({ block: "end" })` pins the
            // last message flush to the bottom while the AI streams, even
            // though `paddingBottom` makes room once scrolling stops.
            scrollPaddingBottom: "32px",
            gap: 3,
        },
    })
}
